import { Container, Box, useMediaQuery, Grid } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useApolloClient } from '@apollo/client';
import { SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import SwiperCarousel from '../shared/SwiperCarousel';
import styles from './styles';
import EirInformation from '../eir-information/EirInformation';
import EirSingleOfferCard from '../eir-offer-card/EirSingleOfferCard';
import RootContext from '../../../util/root.context';
import { SERVICE_GROUPS } from '../shared/Constants';
import EirOfferCard from '../eir-offer-card/EirOfferCard';
import { addAddOn, getCompatibleAddons, onLoadAddons, removeAddOn } from './comaptibeAddons.util';
import { getLocalStorage } from '../../hooks/useLocalStorage';

const { DUALPLAY } = SERVICE_GROUPS;
/**
 * @name EirCompatibleAddonsContainer - jsx component for compatible addons
 * @param {object} cmsData - Strapi data
 * @param {string} id - Id of the container
 * @param {boolean} isPrequaled - Prequal status
 * @param {boolean} loading - Loading status
 * @param {string} OfferCode - Offer code of the tariff added to cart
 * @param {Array} compatibleAddons - compatible addons returned
 * @returns {JSX.Element} - Returns compatible addons component
 */
const EirCompatibleAddonsContainer = ({
  cmsData,
  id,
  isPrequaled,
  loading,
  OfferCode,
  compatibleAddons,
  setCompatibleAddons,
  setLoadingAddon,
}) => {
  const theme = useTheme();
  const style = styles(theme);
  const swiperRef = useRef(null);
  const graphql = useApolloClient();
  const { state: rootState, dispatch } = useContext(RootContext);
  const [showLoader, setShowLoader] = useState('');
  const [offerSelected, setOfferSelected] = useState(null);
  const xs = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current && compatibleAddons?.length > 0) {
      scrollRef.current.scrollIntoView();
    }
  }, [compatibleAddons, OfferCode]);

  const getAddons = async () => {
    const offerId = await rootState.cart?.offers?.filter(
      offer => offer.baseOffer?.serviceGroup === DUALPLAY
    )[0]?.id;
    const data = await getCompatibleAddons(offerId);
    setCompatibleAddons(data);
    setLoadingAddon(false);
  };
  useEffect(() => {
    if (getLocalStorage('PROSPECTUUID')) getAddons();
  }, [OfferCode]);

  useEffect(() => {
    onLoadAddons(rootState, setOfferSelected);
  }, [rootState]);

  return (
    compatibleAddons?.length > 0 && (
      <Container sx={{ ...styles(theme).addonContainer, pr: 0 }} ref={scrollRef}>
        {isPrequaled && (
          <Container id={id} sx={styles(theme.addonBox)} maxWidth='md' disableGutters={xs}>
            <Box sx={styles(theme).addOnInfo}>
              <EirInformation
                title={cmsData?.EirInformation_Addon_Title?.title}
                description={cmsData?.EirInformation_Addon_Title?.description}
                style={{
                  title: styles(theme).title,
                  description: { color: theme.palette.primary.textColor3 },
                }}
              />
            </Box>
            {loading
              ? Array(1)
                  .fill()
                  .map((_, index) => <EirSingleOfferCard loading index={index} />)
              : compatibleAddons &&
                compatibleAddons?.length > 0 &&
                compatibleAddons?.length === 1 &&
                !xs &&
                compatibleAddons?.map((addon, i) => (
                  <Grid key={i} item xs={12}>
                    <EirSingleOfferCard
                      topTag={addon?.topTag}
                      preTitle={addon?.preTitle}
                      title={addon?.title}
                      headerImage={addon?.image}
                      singleTariffHeaderImage={addon?.singleTariffImage}
                      singleHeaderImgDescription={addon.singleHeaderImgDescription}
                      offerSelected={addon.code === offerSelected}
                      code={addon?.code}
                      loading={loading}
                      sash={addon?.sash}
                      description={addon?.description}
                      descriptions={addon?.descriptions}
                      index={i}
                      color='secondary'
                      price={addon.price}
                      contractLength={addon.contractLength}
                      selectButton={{
                        title: 'Select Offer',
                        action: () =>
                          addon.code === offerSelected
                            ? removeAddOn(
                                graphql,
                                addon?.code,
                                rootState,
                                setShowLoader,
                                setOfferSelected,
                                dispatch
                              )
                            : addAddOn(
                                graphql,
                                addon?.code,
                                rootState,
                                setShowLoader,
                                setOfferSelected,
                                dispatch
                              ),
                      }}
                      isAddon
                      showLoader={showLoader}
                    />
                  </Grid>
                ))}
            {compatibleAddons &&
              ((compatibleAddons?.length > 0 && xs) || compatibleAddons?.length > 1) && (
                <SwiperCarousel
                  settings={{
                    className:
                      'content-carousel-broadband content-carousel-broadband-tv-offer-card',
                    pagination: { enable: true, clickable: true },
                    modules: [Pagination],
                    initialSlide: 0,
                    updateOnWindowResize: true,
                    slidesPerView: 'auto',
                    breakpoints: {
                      320: {
                        slidesPerView: 1,
                      },
                    },
                  }}
                  sliderRef={swiperRef}
                  style={style.offerSlider.swiper}
                >
                  {loading
                    ? Array(1)
                        .fill()
                        .map((_, index) => <EirOfferCard loading index={index} />)
                    : compatibleAddons &&
                      compatibleAddons?.length > 0 &&
                      compatibleAddons?.map(
                        (addon, i) =>
                          ((compatibleAddons?.length > 0 && xs) ||
                            compatibleAddons?.length > 1) && (
                            <SwiperSlide key={i}>
                              <EirOfferCard
                                slideNumber={i}
                                topTag={addon?.topTag}
                                preTitle={addon?.preTitle}
                                title={addon?.title}
                                headerImage={addon?.image}
                                singleTariffHeaderImage={addon?.singleTariffImage}
                                headerImgDescription={addon?.headerImgDescription}
                                offerSelected={addon.code === offerSelected}
                                code={addon?.code}
                                loading={loading}
                                sash={addon?.sash}
                                description={addon?.description}
                                descriptions={addon?.descriptions}
                                index={i}
                                color='secondary'
                                price={addon.price}
                                contractLength={addon.contractLength}
                                selectButton={{
                                  title: 'Select Offer',
                                  action: () =>
                                    addon.code === offerSelected
                                      ? removeAddOn(
                                          graphql,
                                          addon?.code,
                                          rootState,
                                          setShowLoader,
                                          setOfferSelected,
                                          dispatch
                                        )
                                      : addAddOn(
                                          graphql,
                                          addon.code,
                                          rootState,
                                          setShowLoader,
                                          setOfferSelected,
                                          dispatch
                                        ),
                                }}
                                isAddon
                                showLoader={showLoader}
                              />
                            </SwiperSlide>
                          )
                      )}
                </SwiperCarousel>
              )}
          </Container>
        )}
      </Container>
    )
  );
};

export default EirCompatibleAddonsContainer;
